import React from 'react';
import { connect } from 'react-redux';
import SearchInput from '../SearchInput';
import { ButtonGroup, Button, Glyphicon, Grid } from 'react-bootstrap';
import FlowLibraryListItem from './FlowLibraryListItem';
import FlowLibraryGridItem from './FlowLibraryGridItem';
import FlowLibraryViewModal from './FlowLibraryViewModal';
import translations from '../../translations';
import { FLOW_LIBRARY_LAYOUTS } from '../../constants';
import { stringContains } from '../../utils/core';
import { fetchFlowLibraryItems, installPlayer } from '../../actions/reduxActions/flowLibrary';
import { importSharedPackage, finishImport, importConflict } from '../../actions/reduxActions/flows';
import EditorActions from '../../actions/editor';
import { addNotification } from '../../actions/reduxActions/notification';
import FlowActions from '../../actions/flow';
import FlowSource from '../../sources/flow';
import GraphActions from '../../actions/graph';
import Confirm from '../generic/confirm';
import DependencyList from '../generic/dependencyList';

/**
 * @description Display each of the available flows in the library
 */
export class FlowLibraryItems extends React.Component {

    static defaultProps = {
        dateFormat: 'D MMM, YYYY',
        maxLength: 200,
        items: [],
        isLoading: false,
    }

    constructor() {
        super();

        this.onSearch = this.onSearch.bind(this);
        this.onListClick = this.onListClick.bind(this);
        this.onGridClick = this.onGridClick.bind(this);
        this.showViewModal = this.showViewModal.bind(this);
        this.hideViewModal = this.hideViewModal.bind(this);
        this.onInstall = this.onInstall.bind(this);
        this.state = {
            searchTerm: '',
            showViewModal: false,
            selectedPackage: null,
            layout: FLOW_LIBRARY_LAYOUTS.GRID,
        };
    }

    componentDidMount() {
        this.props.fetchFlowLibraryItems();
    }

    componentDidUpdate() {
        // After import we 'redirect' the user to the newly imported Flow in a new tab ready to edit.
        // This code is based on components/admin.js openFlow() - i.e. what happens when clicking on the Edit flow outcome
        if (this.props.package.importSuccess) {
            this.props.finishImport();

            const flowId = this.props.package.importSuccess.id.id;
            FlowActions.open(flowId);
            EditorActions.openFlow(flowId, this.props.package.importSuccess.developerName);

            GraphActions.setLoading(flowId);
            FlowSource.getLatest(flowId, this.props.user.tenantId, this.props.user.authenticationToken)
                .then(() => {
                    GraphActions.get(flowId, this.props.user.tenantId, this.props.user.authenticationToken);
                });
        }
    }

    onSearch(term) {
        this.setState({ searchTerm: term });
    }

    onListClick() {
        this.setState({ layout: FLOW_LIBRARY_LAYOUTS.LIST });
    }

    onGridClick() {
        this.setState({ layout: FLOW_LIBRARY_LAYOUTS.GRID });
    }

    /**
     * @param {object} item the item to install
     * @param {boolean} force if true import regardless of any conflicts, otherwise honour conflicts
     */
    onInstall(item, force = false) {
        this.props.importSharedPackage(item.sharingToken, this.props.user.tenantId, this.props.user.authenticationToken, force);
        if (item.preferredPlayer) {
            this.props.installPlayer(item, this.props.user.tenantId, this.props.user.authenticationToken);
        }
        this.hideViewModal(item);
    }

    showViewModal(item) {
        this.setState({
            showViewModal: true,
            selectedPackage: item,
        });
    }

    hideViewModal(item = null) {
        this.setState({
            showViewModal: false,
            selectedPackage: this.props.package.importConflict ? null : item,
        });
    }

    /**
     * @description Reduce items to those matching the search terms
     *
     * Note we are performing client side filtering because the result set is generally very small. < 100 rows.
     *
     * @param {any} items
     * @return filtered items
     */
    filterItems(items) {

        if (!this.state.searchTerm) {
            return items;
        }

        return items.filter(el => stringContains(el.name, this.state.searchTerm, false) ||
            stringContains(el.description, this.state.searchTerm, false),
        this);
    }

    render() {
        const list = (
            <span>
                <Glyphicon glyph="th-list" />
                {translations.FL_layout_list}
            </span>
        );
        const grid = (
            <span>
                <Glyphicon glyph="th" />
                {translations.FL_layout_grid}
            </span>
        );

        // Limit our result set based on search terms
        const filteredItems = this.filterItems(this.props.items);

        const installModal = this.state.showViewModal ?
            (
                <FlowLibraryViewModal
                    item={this.state.selectedPackage}
                    hideModal={this.hideViewModal}
                    dateFormat={this.props.dateFormat}
                    onInstall={() => this.onInstall(this.state.selectedPackage)}
                />
            ) : null;

        const dependencyList = this.props.package.importConflict ?
            <DependencyList dependents={this.props.package.importConflictDependents} /> : null;

        const confirmOverwrite = {
            title: translations['confirm overwrite title'],
            messages: [
                translations['confirm overwrite body 1'],
                translations['confirm overwrite body 2'],
                dependencyList,
                translations['confirm overwrite body 3'],
                translations['confirm overwrite body 4'],
            ],
            onCancel: () => this.props.importConflict(false),
            onConfirm: () => this.onInstall(this.state.selectedPackage, true),
            show: this.props.package.importConflict,
        };

        return (
            <div className="flow-library-items">
                {installModal}
                <Confirm {...confirmOverwrite} />
                <div className="clearfix header">
                    <SearchInput onSearch={this.onSearch} />
                    <ButtonGroup className="layout">
                        <Button
                            className="grid-btn"
                            disabled={this.state.layout === FLOW_LIBRARY_LAYOUTS.GRID}
                            onClick={this.onGridClick}
                        >
                            {grid}
                        </Button>
                        <Button
                            className="list-btn"
                            disabled={this.state.layout === FLOW_LIBRARY_LAYOUTS.LIST}
                            onClick={this.onListClick}
                        >
                            {list}
                        </Button>
                    </ButtonGroup>
                </div>
                <hr />

                {filteredItems.length ?
                    (
                        <Grid fluid className="catalogue">
                            {this.state.layout === FLOW_LIBRARY_LAYOUTS.GRID ?
                                filteredItems.map(item => (
                                    <FlowLibraryGridItem
                                        key={item.sharingToken}
                                        item={item}
                                        onView={() => this.showViewModal(item)}
                                        onInstall={() => this.onInstall(item)}
                                        dateFormat={this.props.dateFormat}
                                    />)) :
                                filteredItems.map(item => (
                                    <FlowLibraryListItem
                                        key={item.sharingToken}
                                        item={item}
                                        onView={() => this.showViewModal(item)}
                                        onInstall={() => this.onInstall(item)}
                                        dateFormat={this.props.dateFormat}
                                    />))
                            }
                        </Grid>
                    ) : <div className="mw-items-empty"><p className="lead">{translations.FL_no_results}</p></div>
                }
            </div>
        );
    }
}

const mapStateToProps = ({ flowLibrary: { items }, flows, currentUser: user }) => ({ items, package: flows, user });

const mapDispatchToProps = {
    addNotification,
    fetchFlowLibraryItems,
    importSharedPackage,
    finishImport,
    installPlayer,
    importConflict,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(FlowLibraryItems);
